// MaturaCylinderHeightClosedTask.js
// Zadanie maturalne – zamknięte
// Walec: dana objętość i promień podstawy, obliczamy wysokość.
// Wzór: V = π r^2 h
//
// Konstrukcja inspirowana oficjalnym zadaniem:
//   „Objętość walca o promieniu podstawy 2 jest równa 16π². …”
//
// Losujemy:
//  - promień r ∈ {2,3,4,5},
//  - współczynnik hCoeff (wysokość to hCoeff lub hCoeff·π),
//  - losowo: objętość ma współczynnik π (V = k·π) LUB π² (V = k·π²).
//    • gdy V ma π² → poprawna odpowiedź ma π,
//    • gdy V ma tylko π → poprawna odpowiedź jest liczbą bez π.
//  - w odpowiedziach zawsze: dwie wartości z π oraz dwie bez π,
//    bez występowania π².

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

// ================== UTILSY ==================

const rndInt = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function heightWithPiLatex(coeff) {
  if (coeff === 1) return "\\pi";
  return `${coeff}\\pi`;
}

function pickDifferentInt(min, max, excluded) {
  const ex = new Set(excluded);
  let x;
  do {
    x = rndInt(min, max);
  } while (ex.has(x));
  return x;
}

// ================== GENERATOR ==================

function createOriginalTask() {
  const options = [
    { latex: "2", hasPi: false, isCorrect: false, label: "A" },
    { latex: "4", hasPi: false, isCorrect: false, label: "B" },
    { latex: "2\\pi", hasPi: true, isCorrect: false, label: "C" },
    { latex: "4\\pi", hasPi: true, isCorrect: true, label: "D" },
  ];

  return {
    type: "MaturaCylinderHeightClosedTask",
    data: {
      r: 2,
      hCoeff: 4,
      volumeCoeff: 16,
      volumeHasPiSquared: true,
      options,
      correctIndex: 3,
      correctHasPi: true,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 27,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // promień podstawy: 2–5 (r = 1 pomijamy)
  const r = rndInt(2, 5);

  // współczynnik wysokości – dodatnia liczba całkowita (2–8)
  const hCoeff = rndInt(2, 8);

  // Czy w objętości ma się pojawić π²?
  const volumeHasPiSquared = Math.random() < 0.5;

  let volumeCoeff;
  let correctHasPi;

  if (volumeHasPiSquared) {
    // V = π r² h, przyjmujemy h = hCoeff·π
    // ⇒ V = π r² (hCoeff·π) = (hCoeff·r²) π²
    volumeCoeff = hCoeff * r * r;
    correctHasPi = true; // poprawna odpowiedź zawiera π
  } else {
    // V = π r² h, przyjmujemy h = hCoeff (bez π)
    // ⇒ V = π r² hCoeff = (hCoeff·r²) π
    volumeCoeff = hCoeff * r * r;
    correctHasPi = false; // poprawna odpowiedź BEZ π
  }

  // ===== Budujemy odpowiedzi: 2 z π, 2 bez π =====

  const minH = 2;
  const maxH = 8;

  let withPiCoefs = [];
  let noPiCoefs = [];

  if (correctHasPi) {
    // poprawna odpowiedź w grupie "z π"
    // z π: {hCoeff, inne}
    const otherWithPi = pickDifferentInt(minH, maxH, [hCoeff]);
    withPiCoefs = [hCoeff, otherWithPi];

    // bez π: dwie fałszywe
    const noPi1 = pickDifferentInt(minH, maxH, [hCoeff, otherWithPi]);
    const noPi2 = pickDifferentInt(minH, maxH, [hCoeff, otherWithPi, noPi1]);
    noPiCoefs = [noPi1, noPi2];
  } else {
    // poprawna odpowiedź w grupie "bez π"
    const otherNoPi = pickDifferentInt(minH, maxH, [hCoeff]);
    noPiCoefs = [hCoeff, otherNoPi];

    // z π: dwie fałszywe
    const withPi1 = pickDifferentInt(minH, maxH, [hCoeff, otherNoPi]);
    const withPi2 = pickDifferentInt(minH, maxH, [
      hCoeff,
      otherNoPi,
      withPi1,
    ]);
    withPiCoefs = [withPi1, withPi2];
  }

  // Teraz składamy 4 opcje: 2 z π, 2 bez π
  const rawOptions = [];

  withPiCoefs.forEach((coef) => {
    rawOptions.push({
      latex: heightWithPiLatex(coef),
      hasPi: true,
      isCorrect: correctHasPi && coef === hCoeff,
    });
  });

  noPiCoefs.forEach((coef) => {
    rawOptions.push({
      latex: `${coef}`,
      hasPi: false,
      isCorrect: !correctHasPi && coef === hCoeff,
    });
  });

  // shuffle + przypisanie liter
  const shuffled = shuffle(rawOptions);
  const options = shuffled.map((opt, idx) => ({
    ...opt,
    label: String.fromCharCode("A".charCodeAt(0) + idx),
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaCylinderHeightClosedTask",
    data: {
      r,
      hCoeff,
      volumeCoeff,
      volumeHasPiSquared,
      options,
      correctIndex,
      correctHasPi,
    },
  };
}

// ================== TREŚĆ ZADANIA ==================

export function renderTask({ data }) {
  const { r, volumeCoeff, volumeHasPiSquared, options } = data;

  const radiusLatex = `r = ${r}`;
  const volumeLatex = volumeHasPiSquared
    ? `${volumeCoeff}\\pi^{2}`
    : `${volumeCoeff}\\pi`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Objętość walca o promieniu podstawy{" "}
        <InlineMath math={radiusLatex} /> jest równa{" "}
        <InlineMath math={volumeLatex} />.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>Wysokość tego walca jest równa:</p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ================== INPUT (ABCD) ==================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ================== CHECK ==================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// ================== POPRAWNA ODPOWIEDŹ ==================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// ================== ODPOWIEDŹ UŻYTKOWNIKA ==================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ================== WYJAŚNIENIE ==================

export function renderExplanation({ data }) {
  const { r, hCoeff, volumeCoeff, volumeHasPiSquared, correctHasPi } = data;

  const rSquared = r * r;
  const finalHeightLatex = correctHasPi
    ? heightWithPiLatex(hCoeff)
    : `${hCoeff}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy ze wzoru na objętość walca:
      </p>
      <BlockMath math={"V = \\pi r^{2} h"} />

      <p>
        W naszym zadaniu{" "}
        <InlineMath math={`r = ${r}`} /> oraz{" "}
        <InlineMath
          math={
            volumeHasPiSquared
              ? `${volumeCoeff}\\pi^{2}`
              : `${volumeCoeff}\\pi`
          }
        />
        .
      </p>

      <p>
        <strong>Krok 2.</strong> Podstawiamy dane do wzoru:
      </p>
      <BlockMath
        math={
          volumeHasPiSquared
            ? `\\pi \\cdot ${r}^{2} \\cdot h = ${volumeCoeff}\\pi^{2}`
            : `\\pi \\cdot ${r}^{2} \\cdot h = ${volumeCoeff}\\pi`
        }
      />

      <p>
        <strong>Krok 3.</strong> Upraszczamy równanie.
      </p>

      {volumeHasPiSquared ? (
        <>
          <p>
            Skracamy po jednej potędze liczby{" "}
            <InlineMath math={"\\pi"} /> po obu stronach:
          </p>
          <BlockMath math={`${rSquared}h = ${volumeCoeff}\\pi`} />
          <p>
            Dzielimy obie strony przez{" "}
            <InlineMath math={`${rSquared}`} />:
          </p>
          <BlockMath
            math={`h = \\dfrac{${volumeCoeff}\\pi}{${rSquared}} = ${finalHeightLatex}`}
          />
        </>
      ) : (
        <>
          <p>
            Dzielimy obie strony przez{" "}
            <InlineMath math={"\\pi"} />, otrzymując:
          </p>
          <BlockMath math={`${rSquared} h = ${volumeCoeff}`} />
          <p>
            Dzielimy obie strony przez{" "}
            <InlineMath math={`${rSquared}`} />:
          </p>
          <BlockMath
            math={`h = \\dfrac{${volumeCoeff}}{${rSquared}} = ${finalHeightLatex}`}
          />
        </>
      )}

      <p>
        Zatem wysokość walca jest równa{" "}
        <InlineMath math={finalHeightLatex} />.
      </p>
    </div>
  );
}
